// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Page d'aide — /aide (31/08/2026).
//
// ⛔ CETTE PAGE NE CRÉE AUCUNE RÈGLE. Elle rend actionnable, en langage métier,
// un dispatch déjà OPPOSABLE : SLA article 8 (priorité → canal → informations à
// fournir, et le format d'objet imposé) et Mentions légales article 10 (les six
// adresses). Toute divergence entre /aide et /sla est un écart contractuel —
// et c'est /aide qui a tort : les documents juridiques font foi.
//
// ⛔ NE PAS y réafficher les délais de réponse de l'article 3.2 du SLA. Hors du
// contexte de leurs exclusions (article 4), ils deviennent une promesse au
// forfait. /aide dit OÙ écrire et QUOI mettre ; /sla dit sous combien de temps.
//
// 🇫🇷 Français seul, aucun `t()` — précédent assumé de `LegalPage.js`, qui ne
// contient pas une seule clé i18n : ces pages sont des documents, pas de l'UI.
// Seul le libellé du menu qui y conduit (`menu_help`) reste traduit.
//
// ⭐ Chaque libellé cité ici a été relevé DANS le code le 31/08/2026, puis
// vérifié vivant par `grep -rl "<clé>" frontend/src --include=*.js | grep -v
// locales`. Un catalogue i18n garde ses clés mortes sans rien casser : le
// backport `b09169a` a laissé 18 clés que plus aucun composant n'appelle, et
// documenter un état qui n'existe pas est le risque propre à cette page.
// ➜ Refaire ce contrôle avant d'ajouter le moindre libellé ci-dessous.

import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Article, Sub, List, DataTable, Callout, EnTeteDocument } from '../components/DocLayout';
import { useAuth } from '../context/AuthContext';
import { envoyerDemandeAide, chargerAnalysesPourAide } from '../services/api';

const CROSS_LINKS = [
  { href: '/cgv', label: 'CGU/CGV' },
  { href: '/avertissement-ia', label: 'Avertissement IA' },
  { href: '/privacy', label: 'Confidentialité' },
  { href: '/mentions-legales', label: 'Mentions légales' },
  { href: '/sla', label: 'SLA' },
];

const SOMMAIRE = [
  { href: '#analyse', label: 'Où en est mon analyse ?' },
  { href: '#refus',   label: 'Mon dépôt a été refusé' },
  { href: '#ecrans',  label: 'À quoi sert chaque écran' },
  { href: '#compte',  label: 'Compte, accès et sécurité' },
  { href: '#ecrire',  label: 'Nous écrire' },
  { href: '#legal',   label: 'Documents contractuels' },
];

const Mail = ({ adresse }) => (
  <a href={'mailto:' + adresse} className="text-blue-700 underline whitespace-nowrap">{adresse}</a>
);

const Objet = ({ p }) => (
  <span className="font-mono text-[11px]">[{p}] mon organisation – description courte</span>
);

// ── Formulaire d’assistance (étape 2, 31/08/2026) ────────────────────────
//
// Ce qu’il achète n’est PAS l’aiguillage — six lignes de tableau se lisent très
// bien — mais le CONTEXTE et la MISE EN FORME : le serveur connaît le compte,
// l’organisation, la licence et l’analyse, compose l’objet au format de
// l’article 8 du SLA et remplit le corps de ce que cet article exige.
// L’utilisateur n’a jamais à savoir ce qu’est un P2.
//
// ⛔ IL NE REMPLACE PAS LES `mailto:` — ils restent affichés juste en dessous.
// Quelqu’un de bloqué au login ne peut pas poster un formulaire authentifié :
// c’est précisément le cas d’usage n° 1 de cette page.
//
// 🇫🇷 Français en dur, comme tout le reste de la page. Un formulaire est de
// l’UI, et `CLAUDE.md` impose `t()` pour l’UI — mais l’exception assumée ici est
// celle de la page entière : traduire les seuls libellés du formulaire dans un
// document intégralement français n’aiderait personne. ⚠️ Si la page est un jour
// traduite, elle doit l’être EN ENTIER, formulaire compris.

// ⚠️ MIROIR EXACT de `_AIDE_SITUATIONS` / `_AIDE_LIBELLES` dans
// `blueprints/contact.py`. Une valeur qui diverge est rejetée en 400 par le
// serveur ; un libellé qui diverge est un écart avec le tableau ci-dessous, donc
// avec l’article 8 du SLA. 🛡️ Garde-fou : `tests/unit/test_aide_situations.py`.
// ⛔ « Un avis me paraît faux » n’y figure pas et ne doit jamais y figurer :
// cette situation part vers la boucle de retour S3, pas vers une boîte mail.
const SITUATIONS_FORMULAIRE = [
  { valeur: 'acces_impossible', libelle: 'Je ne peux plus accéder au service' },
  { valeur: 'fonction_bloquee', libelle: 'Une fonction majeure est bloquée (dépôt, analyse, export…)' },
  { valeur: 'bug_question',     libelle: "Bug mineur, question d'usage, demande d'évolution" },
  { valeur: 'quota_facture',    libelle: 'Quota, facture, licence, agences' },
  { valeur: 'rgpd',             libelle: 'Mes données personnelles (RGPD)' },
  { valeur: 'securite',         libelle: 'Faille de sécurité' },
];

const champStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 6,
  border: '1px solid #d1d5db', background: 'white', color: '#111827',
};

const FormulaireAide = () => {
  // 💣 Fonction, pas booléen — voir `EnTete` ci-dessus. On la réduit à un
  // booléen tout de suite : dépendre de la fonction elle-même relancerait
  // l’effet à chaque rendu du provider, qui la recrée.
  const { isAuthenticated } = useAuth();
  const connecte = isAuthenticated();
  const [situation, setSituation]     = useState('');
  const [description, setDescription] = useState('');
  const [analysisId, setAnalysisId]   = useState('');
  const [analyses, setAnalyses]       = useState([]);
  // null = on ne sait pas encore ; false = pas de session utilisable.
  const [session, setSession]         = useState(null);
  const [etat, setEtat]               = useState('repos'); // repos | envoi | ok | erreur
  const [retour, setRetour]           = useState(null);

  // Ce chargement fait DEUX choses : il remplit la liste déroulante, et il sert
  // de sonde de session. ⚠️ `isAuthenticated` ne vaut que « un jeton existe dans
  // le navigateur » — pas « il est encore valable ». Sans cette sonde, un jeton
  // périmé affichait un formulaire qui échouait à l’envoi.
  // ⛔ `chargerAnalysesPourAide` et NON `getAnalyses` : voir son commentaire dans
  // `services/api.js` — l’intercepteur global éjecte vers /login sur 401.
  useEffect(() => {
    if (!connecte) { setSession(false); return; }
    let annule = false;
    chargerAnalysesPourAide().then(r => {
      if (annule) return;
      setSession(r.session);
      setAnalyses(r.analyses);
    });
    return () => { annule = true; };
  }, [connecte]);

  // Sans session utilisable, on n’affiche RIEN : les `mailto:` ci-dessous sont
  // alors le canal, et ils suffisent. Afficher un formulaire qui répondrait 401
  // serait pire que ne rien afficher. `null` (on ne sait pas encore) se comporte
  // comme `false` : mieux vaut un formulaire qui apparaît tard qu’un qui échoue.
  if (session !== true) return null;

  const envoyer = async e => {
    e.preventDefault();
    if (!situation || !description.trim()) return;
    setEtat('envoi');
    setRetour(null);
    try {
      const r = await envoyerDemandeAide({ situation, description, analysisId });
      setRetour(r);
      setEtat('ok');
      setSituation(''); setDescription(''); setAnalysisId('');
    } catch (err) {
      setRetour({ message: err.message, destinataire: err.destinataire });
      setEtat('erreur');
    }
  };

  if (etat === 'ok') {
    return (
      <Callout tone="blue">
        <p><strong>Votre demande a bien été transmise.</strong> Une confirmation vient de
        vous être envoyée par courriel.</p>
        {retour?.objet && (
          <p className="mt-2 text-xs">Objet transmis : <span className="font-mono">{retour.objet}</span></p>
        )}
        <p className="mt-2">
          <button type="button" onClick={() => setEtat('repos')} className="underline">
            Écrire un autre message
          </button>
        </p>
      </Callout>
    );
  }

  return (
    <form onSubmit={envoyer} className="border border-gray-200 rounded p-4 bg-gray-50 my-3 space-y-3">
      <p className="text-sm font-semibold text-gray-900">Écrivez-nous depuis cette page</p>
      <p className="text-xs text-gray-500">Nous joignons automatiquement votre compte, votre
      organisation et l’analyse que vous désignez, et nous mettons l’objet au format attendu.
      Vous n’avez rien d’autre à préciser.</p>

      <label className="block text-sm">
        <span className="text-gray-700">Ma situation</span>
        <select value={situation} onChange={e => setSituation(e.target.value)}
                style={champStyle} className="mt-1" required>
          <option value="">Choisissez…</option>
          {SITUATIONS_FORMULAIRE.map(s => (
            <option key={s.valeur} value={s.valeur}>{s.libelle}</option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        <span className="text-gray-700">Ce qui se passe</span>
        <textarea value={description} onChange={e => setDescription(e.target.value)}
                  rows={5} maxLength={4000} style={champStyle} className="mt-1" required
                  placeholder="Ce que vous attendiez, ce que vous avez obtenu, et les étapes pour le reproduire." />
      </label>

      <label className="block text-sm">
        <span className="text-gray-700">Analyse concernée <em className="text-gray-400">(facultatif)</em></span>
        <select value={analysisId} onChange={e => setAnalysisId(e.target.value)}
                style={champStyle} className="mt-1">
          <option value="">Aucune en particulier</option>
          {analyses.map(a => (
            <option key={a.id} value={a.id}>{a.filename}</option>
          ))}
        </select>
      </label>

      {etat === 'erreur' && (
        <div className="border-l-4 border-red-400 bg-red-50 text-red-900 rounded p-3 text-sm">
          <p>{retour?.message || "Votre message n'a pas pu être transmis."}</p>
          {retour?.destinataire && (
            <p className="mt-1">
              Écrivez-nous directement à{' '}
              <a href={'mailto:' + retour.destinataire} className="underline">{retour.destinataire}</a>.
            </p>
          )}
        </div>
      )}

      <button type="submit" disabled={etat === 'envoi' || !situation || !description.trim()}
              className="px-4 py-2 rounded text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 disabled:opacity-50">
        {etat === 'envoi' ? 'Envoi…' : 'Envoyer ma demande'}
      </button>
    </form>
  );
};

// ⚠️ L’en-tête vit dans `components/DocLayout.js` (`EnTeteDocument`) depuis le
// 31/08/2026 : les cinq pages légales portaient exactement le même défaut
// d’affichage — « Connexion » montré à quelqu’un de connecté — signalé par Marc
// sur `/aide` puis sur `/cgv`. Un seul endroit, six pages corrigées.

const HelpPage = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col">
    <EnTeteDocument />

    <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Aide et assistance</h1>
      <p className="text-xs text-gray-500 mb-6">EVALENS — Normascan · Mise à jour : 31/08/2026</p>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">

        <Callout tone="blue">
          <p>Cette page décrit ce que Normascan fait <strong>aujourd'hui</strong> : comment suivre
          une analyse, pourquoi un dépôt peut être refusé, à quoi sert chaque écran, et à qui
          écrire selon la situation.</p>
          <p className="mt-2">Normascan est un <strong>outil d'aide à la décision</strong>. La
          portée exacte de ses résultats et les précautions qui s'attachent à leur exploitation
          sont décrites dans l'<Link href="/avertissement-ia" className="underline">Avertissement
          IA</Link>, qui fait partie intégrante du contrat.</p>
        </Callout>

        <nav className="my-6 border border-gray-200 rounded p-4 bg-gray-50">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sommaire</p>
          <ul className="space-y-1 text-sm">
            {SOMMAIRE.map((s, i) => (
              <li key={s.href}>
                <a href={s.href} className="text-blue-700 hover:underline">{i + 1}. {s.label}</a>
              </li>
            ))}
          </ul>
        </nav>

        {/* ── 1. Suivi d'une analyse ─────────────────────────────────────── */}
        <div id="analyse">
          <Article title="1. Où en est mon analyse ?">
            <p>Chaque analyse porte un état, affiché sur sa carte dans <strong>Mes analyses</strong>.
            Il y en a quatre, et un seul appelle une action de votre part.</p>
            <DataTable
              headers={['État affiché', 'Ce que cela signifie', 'Ce que vous pouvez faire']}
              rows={[
                [
                  'En attente',
                  "Votre rapport est dans la file. Le nombre d'analyses traitées simultanément est plafonné, pour qu'aucune ne ralentisse les autres.",
                  "Rien : l'analyse démarrera seule. Vous pouvez fermer la page, et même votre navigateur — la file est conservée par le serveur : une analyse en attente repart d'elle-même après une mise à jour ou un redémarrage.",
                ],
                [
                  'En cours',
                  "Le traitement se déroule en douze étapes, affichées au fur et à mesure sous la barre de progression.",
                  "Rien. La progression se met à jour d'elle-même, sans recharger la page.",
                ],
                [
                  'Terminé',
                  "L'analyse est disponible : rapport PDF et export Excel se téléchargent depuis la carte.",
                  "Télécharger vos documents — et, si un avis vous paraît faux, nous le signaler (section 5).",
                ],
                [
                  'Erreur',
                  "Le traitement s'est interrompu avant d'aboutir.",
                  "Redéposer le document. Si l'erreur se reproduit sur le même fichier, écrivez-nous en indiquant l'identifiant de l'analyse et l'heure.",
                ],
              ]}
            />
            <Sub title="Les douze étapes du traitement">
              <p className="text-xs text-gray-500">Dans l'ordre : Extraction · Structuration ·
              Normalisation · Classification · Identification · Cohérence · Plans · Validation ·
              Norma Vision · Évaluation · HTML · Excel. Les deux dernières produisent vos
              livrables ; les précédentes lisent le rapport et instruisent les règles.</p>
            </Sub>
          </Article>
        </div>

        {/* ── 2. Refus au dépôt ──────────────────────────────────────────── */}
        <div id="refus">
          <Article title="2. Mon dépôt a été refusé">
            <p>Un refus survient <strong>au dépôt</strong>, avant tout traitement : il n'entame donc
            jamais votre quota. Quatre causes possibles.</p>

            <Sub title="« Vous avez utilisé toutes vos analyses »">
              <p>Votre quota est épuisé : le dépôt d'un rapport, l'ajout d'un document à un projet
              et le démarrage d'une analyse sont bloqués jusqu'à son renouvellement. La zone de
              dépôt affiche alors « Dépôt indisponible — quota épuisé ».</p>
              <p>➜ Le bouton <strong>« Nous contacter »</strong> de ce message ouvre la demande de
              quota supplémentaire ; voir aussi la section 5, ligne « Quota, facture, licence ».</p>
            </Sub>

            <Sub title="« Ce document ne semble pas être un rapport de repérage amiante »">
              <p>Le fichier n'a pas été reconnu comme un rapport de repérage. Vérifiez que vous
              déposez bien le rapport lui-même, et non une annexe, un devis, un plan ou un
              procès-verbal de laboratoire isolé.</p>
            </Sub>

            <Sub title="« Ce document dépasse la limite de traitement autorisée »">
              <p>Le PDF est trop lourd pour être traité en une fois. Réduisez sa taille ou
              découpez-le, puis redéposez-le.</p>
            </Sub>

            <Sub title="La mission n'est pas dans le champ d'analyse">
              <p>Normascan analyse les <strong>repérages avant travaux (RAAT)</strong> et les
              <strong> repérages avant démolition</strong> sur immeuble bâti. Les autres missions
              amiante relèvent d'un référentiel différent et ne peuvent pas être évaluées avec le
              même : dossier technique amiante (DTA), fiche récapitulative de DTA, dossier amiante
              parties privatives (DAPP), évaluation périodique de l'état de conservation.</p>
              <p>Le cas des <strong>enrobés et HAP</strong> est distinct : ce n'est pas un hors
              périmètre, mais un référentiel que nous n'avons pas encore instruit — plusieurs
              cadres réglementaires et normatifs y coexistent.</p>
              <p className="text-xs text-gray-500">Ces missions ne sont pas annoncées « en cours de
              développement » et nous ne nous engageons sur aucune date. Si l'une d'elles compte
              pour votre activité, dites-le nous : c'est ce qui nous sert à arbitrer.</p>
            </Sub>
          </Article>
        </div>

        {/* ── 3. Les écrans ──────────────────────────────────────────────── */}
        <div id="ecrans">
          <Article title="3. À quoi sert chaque écran">
            <DataTable
              headers={['Écran', "Ce qu'on y fait"]}
              rows={[
                [
                  'NORMA',
                  "L'écran d'accueil : on y dépose un rapport et on suit l'analyse en cours. C'est le point de départ normal.",
                ],
                [
                  'Mes analyses',
                  "L'historique complet. Chaque carte porte l'état de l'analyse, le décompte des avis favorables, sous réserve et défavorables, les téléchargements — et le lien pour signaler un avis erroné.",
                ],
                [
                  'Projets',
                  "Pour regrouper les rapports et les documents d'une même opération. Deux types de mission : RAAT — Repérage avant travaux, et RAAD — Diagnostic avant démolition.",
                ],
                [
                  'Mes statistiques',
                  "Vos propres chiffres : volume d'analyses, première et dernière analyse, répartition des avis. Vos données seules — jamais celles d'un autre compte.",
                ],
                [
                  'Espace client',
                  "Visible uniquement si vous administrez la licence de votre organisation : sièges, invitations, organisation et statistiques consolidées.",
                ],
                [
                  'Paramètres',
                  "Cinq onglets : Profil, Sécurité (mot de passe et double authentification), Abonnement, Préférences, et Mes données (vos droits RGPD).",
                ],
                [
                  'Nouveautés',
                  "Le registre des mises à jour. La fenêtre affichée à la connexion s'y retrouve en entier une fois fermée.",
                ],
              ]}
            />
          </Article>
        </div>

        {/* ── 4. Compte ──────────────────────────────────────────────────── */}
        <div id="compte">
          <Article title="4. Compte, accès et sécurité">
            <Sub title="J'ai oublié mon mot de passe">
              <p>Depuis l'écran de connexion, utilisez « Mot de passe oublié » : un lien de
              réinitialisation vous est envoyé par courriel. Aucun mot de passe ne vous sera jamais
              demandé par téléphone ni par courriel.</p>
            </Sub>
            <Sub title="J'ai été déconnecté sans rien faire">
              <p>Une session dure <strong>24 heures</strong>, puis expire : il suffit de se
              reconnecter. Rien n'est perdu — une analyse lancée se poursuit côté serveur pendant
              que vous êtes déconnecté.</p>
            </Sub>
            <Sub title="Double authentification">
              <p>Depuis <strong>Paramètres → Sécurité</strong>, vous pouvez activer
              l'authentification à deux facteurs : scannez le QR code avec une application
              d'authentification (Google Authenticator, Aegis…), puis confirmez avec le code à six
              chiffres qu'elle affiche.</p>
              <p>Des <strong>codes de récupération</strong> vous sont alors remis. Conservez-les en
              lieu sûr : chacun ne peut servir qu'une seule fois, et ce sont eux qui vous rendront
              l'accès si vous perdez votre téléphone.</p>
              <p className="text-xs text-gray-500">Cette activation est <strong>obligatoire</strong>
              {' '}pour les comptes d'administration technique : si votre rôle l'exige, l'application
              la demande à la connexion, avant tout accès.</p>
            </Sub>
            <Sub title="J'ai reçu une invitation">
              <p>Le lien reçu par courriel conduit à la création de votre compte au sein de
              l'organisation qui vous a invité. Il est nominatif et à usage unique.</p>
            </Sub>
          </Article>
        </div>

        {/* ── 5. Nous écrire ─────────────────────────────────────────────── */}
        <div id="ecrire">
          <Article title="5. Nous écrire">
            <p>Choisissez la <strong>situation</strong> qui vous correspond : elle détermine
            l'adresse et l'objet du message. Un objet correctement formé nous fait gagner le
            premier aller-retour.</p>

            <FormulaireAide />

            <p className="text-xs text-gray-500">Le tableau ci-dessous reste valable en toutes
            circonstances — et c’est le seul recours si vous ne parvenez pas à vous connecter.</p>
            <DataTable
              headers={['Ma situation', "J'écris à", 'Objet du message']}
              rows={[
                [
                  'Je ne peux plus accéder au service',
                  <Mail adresse="urgent@normascan.fr" />,
                  <Objet p="P1" />,
                ],
                [
                  'Une fonction majeure est bloquée (dépôt, analyse, export…)',
                  <Mail adresse="support@normascan.fr" />,
                  <Objet p="P2" />,
                ],
                [
                  <strong>Un avis me paraît faux, une donnée est mal lue</strong>,
                  <em>le lien « Signaler un avis », dans l'application</em>,
                  '—',
                ],
                [
                  "Bug mineur, question d'usage, demande d'évolution",
                  <Mail adresse="support@normascan.fr" />,
                  <Objet p="P3" />,
                ],
                [
                  'Quota, facture, licence, agences',
                  <Mail adresse="contact@evalens.fr" />,
                  '—',
                ],
                [
                  'Mes données personnelles (RGPD)',
                  <Mail adresse="privacy@evalens.fr" />,
                  '—',
                ],
                [
                  'Faille de sécurité',
                  <Mail adresse="security@normascan.fr" />,
                  '—',
                ],
              ]}
            />
            <p className="text-xs text-gray-500">Le format d'objet est celui de l'article 8 du{' '}
              <Link href="/sla" className="underline">SLA</Link> : la mention entre crochets, le
              nom de votre organisation, puis une description courte — par exemple
              « [P2] Bureau MOA Lyon – Export Excel bloqué depuis 14h ». Les délais de réponse
              attachés à chaque niveau, et les situations qu'ils excluent, figurent dans le SLA :
              ils n'ont de sens qu'avec leurs exclusions.</p>

            <Sub title="⭐ Un avis vous paraît faux ? Ne nous écrivez pas : signalez-le.">
              <p>C'est le canal le plus utile du produit, et le seul qui améliore réellement
              l'analyse. Ouvrez <strong>Mes analyses</strong> et repérez l'analyse concernée : sous
              les trois compteurs de sa carte (favorable / sous réserve / défavorable), le lien
              <strong> « 🚩 Signaler un avis »</strong> ouvre le panneau de signalement.</p>
              <p>Vous y désignez la règle en cause et expliquez pourquoi son avis vous paraît
              erroné. <strong>Votre rapport n'est pas modifié</strong> : un signalement ne corrige
              pas l'analyse déjà rendue, il corrige Normascan. Le même panneau permet aussi de
              signaler un écart que Normascan <em>n'aurait pas vu</em>.</p>
              <p className="text-xs text-gray-500">L'explication fait toute la valeur du
              signalement : c'est elle qui permettra de trancher entre une
              règle à corriger et un rapport atypique.</p>
            </Sub>

            <Sub title="Ce qu'il faut nous donner">
              <p>Quel que soit le canal, ces éléments évitent presque toujours un échange
              supplémentaire :</p>
              <List items={[
                'Ce que vous attendiez, et ce que vous avez obtenu',
                'Les étapes pour reproduire le problème',
                "L'identifiant de l'analyse concernée",
                "L'heure à laquelle cela s'est produit",
                'Une capture d’écran',
              ]} />
            </Sub>
          </Article>
        </div>

        {/* ── 6. Documents contractuels ──────────────────────────────────── */}
        <div id="legal">
          <Article title="6. Documents contractuels">
            <p>Cette page est une aide à l'usage. En cas de divergence, <strong>ce sont les
            documents ci-dessous qui font foi.</strong></p>
            <List items={[
              <><Link href="/cgv" className="underline">CGU/CGV</Link> — conditions d'accès, offres et facturation.</>,
              <><Link href="/avertissement-ia" className="underline">Avertissement IA</Link> — la portée exacte des résultats d'analyse et vos obligations dans leur exploitation.</>,
              <><Link href="/privacy" className="underline">Politique de confidentialité</Link> — données traitées, durées de conservation, vos droits.</>,
              <><Link href="/mentions-legales" className="underline">Mentions légales</Link> — l'éditeur, l'hébergeur et l'ensemble des adresses de contact.</>,
              <><Link href="/sla" className="underline">SLA</Link> — disponibilité, priorités d'incident et délais de réponse, avec leurs exclusions.</>,
            ]} />
          </Article>
        </div>

        <p className="text-xs text-gray-400 text-center mt-6">EVALENS SASU — Normascan — © 2026-2028 Tous droits réservés</p>
      </div>

      <div className="mt-6 text-sm text-gray-500 flex flex-wrap gap-4">
        {CROSS_LINKS.map(link => (
          <Link key={link.href} href={link.href} className="hover:underline">{link.label}</Link>
        ))}
      </div>
    </main>
  </div>
);

export default HelpPage;
